import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { PinnedContext, PromptDTO } from "@shaarp/common";
import { useCallback } from "react";

export type PinnedPromptsByContext = Record<PinnedContext, string[]>;

type UsePinnedPromptsEditorOptions = {
  state: PinnedPromptsByContext;
  setState: (
    updater: (prev: PinnedPromptsByContext) => PinnedPromptsByContext,
  ) => void;
  /** Re-fetches the authoritative state after a mutation that isn't applied optimistically (add/remove). */
  refetch: () => Promise<void>;
  buildAddUrl: (context: PinnedContext, promptId: string) => string;
  buildRemoveUrl: (context: PinnedContext, promptId: string) => string;
  buildReorderUrl: (context: PinnedContext) => string;
};

/**
 * The add/remove/reorder mutations shared by the team-default editor and the
 * per-member editor — the two admin screens differ in how they fetch their
 * initial state (one combined default DTO vs. two per-context arrays) and in
 * what else sits on the page, but every mutation ends up hitting a `POST
 * .../:promptId?context=`, `DELETE .../:promptId?context=`, or `PUT
 * .../reorder` shaped exactly the same way, so only the URL builders differ
 * between the two backends this talks to.
 */
export function usePinnedPromptsEditor({
  state,
  setState,
  refetch,
  buildAddUrl,
  buildRemoveUrl,
  buildReorderUrl,
}: UsePinnedPromptsEditorOptions) {
  const api = useApi();
  const toastError = useToastError();

  const handleAdd = useCallback(
    async (context: PinnedContext, prompt: PromptDTO) => {
      try {
        await api.post(buildAddUrl(context, prompt.id));
        await refetch();
      } catch {
        toastError();
      }
    },
    [api, buildAddUrl, refetch, toastError],
  );

  const handleRemove = useCallback(
    async (context: PinnedContext, promptId: string) => {
      try {
        await api.delete(buildRemoveUrl(context, promptId));
        await refetch();
      } catch {
        toastError();
      }
    },
    [api, buildRemoveUrl, refetch, toastError],
  );

  const handleMove = useCallback(
    async (context: PinnedContext, index: number, direction: -1 | 1) => {
      const target = index + direction;
      const ids = state[context];
      if (target < 0 || target >= ids.length) return;

      const reordered = [...ids];
      [reordered[index], reordered[target]] = [
        reordered[target],
        reordered[index],
      ];

      const previous = state;
      setState((prev) => ({ ...prev, [context]: reordered }));

      try {
        await api.put(buildReorderUrl(context), {
          orderedPromptIds: reordered,
          context,
        });
      } catch {
        toastError();
        setState(() => previous);
      }
    },
    [api, buildReorderUrl, setState, state, toastError],
  );

  return { handleAdd, handleRemove, handleMove };
}
